/**
 * Estate Legacy Contact Service
 *
 * Manages legacy contact records stored as estate_vault_contacts
 * with role='legacy_contact'. These represent trusted individuals
 * who will have access to the owner's estate data upon verified death.
 *
 * @module services/EstateLegacyContactService
 */

import { supabase } from '@xanectech/supabase-client/supabase-client';
import { createLogger, getErrorMessage } from '@xanectech/lib';
import { decryptValue } from '../utils/personal-data/encryption';
import type { Database } from '@xanectech/types';
import { EstateContactDocumentWorkflowService } from './EstateContactDocumentWorkflowService';

const logger = createLogger('EstateLegacyContactService');

// ============================================
// Row type aliases
// ============================================

type EstateContact =
  Database['public']['Tables']['estate_vault_contacts']['Row'];
type EstateContactInsert =
  Database['public']['Tables']['estate_vault_contacts']['Insert'];
type ContactPermission =
  Database['public']['Tables']['estate_vault_contact_permissions']['Row'];
type ContactPermissionInsert =
  Database['public']['Tables']['estate_vault_contact_permissions']['Insert'];

type CategoryEntry = Database['public']['Tables']['category_entries']['Row'];
type CategoryFieldData =
  Database['public']['Tables']['category_field_data']['Row'];
type FieldDefinition = Database['public']['Tables']['field_definitions']['Row'];

interface LegacyContactCandidate {
  sourceEntryId: string;
  contactName: string;
  contactEmail: string;
  relationship: string;
  relationshipDetails: string | null;
  priorityOrder: number | null;
}

const CONTACTS_CATEGORY_TYPE = 'contacts';
const LEGACY_FIELD_NAME_CANDIDATES = ['legacy_contact', 'is_legacy_contact'];
const LEGACY_SYNC_COOLDOWN_MS = 30000;

const asNormalizedText = (value: string | null | undefined): string =>
  String(value ?? '').trim();

const safeParseNumber = (value: string | null | undefined): number | null => {
  const normalized = asNormalizedText(value);
  if (!normalized) return null;
  const parsed = Number.parseInt(normalized, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

// ============================================
// DTOs
// ============================================

export interface CreateLegacyContactPayload {
  contact_name: string;
  contact_email: string;
  relationship: string;
  relationship_details?: string | null;
  priority_order?: number | null;
  /** ISO 3166-1 alpha-2 country code for document requirement evaluation. */
  country_code?: string | null;
}

export interface UpdateLegacyContactPayload {
  contact_name?: string;
  contact_email?: string;
  relationship?: string;
  relationship_details?: string | null;
  priority_order?: number | null;
  /** ISO 3166-1 alpha-2 country code for document requirement evaluation. */
  country_code?: string | null;
}

export type LegacyContact = EstateContact;
export type LegacyContactPermission = ContactPermission;

// ============================================
// Service
// ============================================

export class EstateLegacyContactService {
  private static readonly lastSyncedByUser = new Map<string, number>();

  private static async tryDecodeValue(value: string | null): Promise<string> {
    if (!value) return '';

    try {
      return await decryptValue(value);
    } catch {
      return value;
    }
  }

  private static buildFieldMapByEntry(
    fieldData: CategoryFieldData[],
  ): Map<string, Map<string, string>> {
    const byEntry = new Map<string, Map<string, string>>();

    for (const row of fieldData) {
      const entryMap =
        byEntry.get(String(row.entry_id)) ?? new Map<string, string>();
      entryMap.set(String(row.field_id), String(row.value_encrypted ?? ''));
      byEntry.set(String(row.entry_id), entryMap);
    }

    return byEntry;
  }

  private static async getLegacyCandidatesFromContactsCategory(
    userId: string,
  ): Promise<LegacyContactCandidate[]> {
    const { data: categories, error: categoriesError } = await supabase
      .from('data_categories')
      .select('id')
      .eq('category_type', CONTACTS_CATEGORY_TYPE)
      .eq('is_active', true);

    if (categoriesError) {
      logger.warn('Failed to load contacts category metadata', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(categoriesError),
      });
      return [];
    }

    const categoryIds = (categories ?? []).map((c) => c.id);
    if (categoryIds.length === 0) return [];

    const { data: entries, error: entriesError } = await supabase
      .from('category_entries')
      .select('id')
      .eq('user_id', userId)
      .in('category_id', categoryIds);

    if (entriesError) {
      logger.warn('Failed to load contacts category entries', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(entriesError),
      });
      return [];
    }

    const entryRows = (entries ?? []) as CategoryEntry[];
    const entryIds = entryRows.map((entry) => entry.id);
    if (entryIds.length === 0) return [];

    const { data: fieldDefinitions, error: fieldsError } = await supabase
      .from('field_definitions')
      .select('id, field_name, field_label')
      .in('category_id', categoryIds)
      .eq('is_active', true);

    if (fieldsError) {
      logger.warn('Failed to load contacts category field definitions', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(fieldsError),
      });
      return [];
    }

    const fields = (fieldDefinitions ?? []) as Pick<
      FieldDefinition,
      'id' | 'field_name' | 'field_label'
    >[];

    const byFieldName = new Map<string, string>();
    let legacyFieldId: string | null = null;
    for (const field of fields) {
      const fieldName = String(field.field_name ?? '').toLowerCase();
      if (fieldName) {
        byFieldName.set(fieldName, field.id);
      }

      const label = String(field.field_label ?? '').toLowerCase();
      const isLegacyField =
        LEGACY_FIELD_NAME_CANDIDATES.includes(fieldName) ||
        (label.includes('legacy') && label.includes('contact'));
      if (!legacyFieldId && isLegacyField) {
        legacyFieldId = field.id;
      }
    }

    const relevantFieldIds = [
      byFieldName.get('first_name'),
      byFieldName.get('last_name'),
      byFieldName.get('email'),
      byFieldName.get('relationship'),
      byFieldName.get('priority_order'),
      byFieldName.get('notes'),
      byFieldName.get('contact_type'),
    ].filter((fieldId): fieldId is string => Boolean(fieldId));

    const { data: fieldDataRows, error: fieldDataError } = await supabase
      .from('category_field_data')
      .select('entry_id, field_id, value_encrypted')
      .in('entry_id', entryIds)
      .in('field_id', relevantFieldIds);

    if (fieldDataError) {
      logger.warn('Failed to load contacts category field values', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(fieldDataError),
      });
      return [];
    }

    const byEntry = this.buildFieldMapByEntry(
      (fieldDataRows ?? []) as CategoryFieldData[],
    );

    const candidates: LegacyContactCandidate[] = [];
    for (const entry of entryRows) {
      const entryValues = byEntry.get(entry.id);
      if (!entryValues) continue;

      const firstName = await this.tryDecodeValue(
        entryValues.get(byFieldName.get('first_name') ?? '') ?? null,
      );
      const lastName = await this.tryDecodeValue(
        entryValues.get(byFieldName.get('last_name') ?? '') ?? null,
      );
      const email = await this.tryDecodeValue(
        entryValues.get(byFieldName.get('email') ?? '') ?? null,
      );
      const relationship = await this.tryDecodeValue(
        entryValues.get(byFieldName.get('relationship') ?? '') ?? null,
      );
      const priority = await this.tryDecodeValue(
        entryValues.get(byFieldName.get('priority_order') ?? '') ?? null,
      );
      const notes = await this.tryDecodeValue(
        entryValues.get(byFieldName.get('notes') ?? '') ?? null,
      );
      const contactType = await this.tryDecodeValue(
        entryValues.get(byFieldName.get('contact_type') ?? '') ?? null,
      );

      const contactEmail = asNormalizedText(email).toLowerCase();
      if (!contactEmail) continue;

      const contactName =
        asNormalizedText(`${firstName} ${lastName}`) ||
        asNormalizedText(await this.tryDecodeValue(String(entry.entry_label)));

      const relationshipValue =
        asNormalizedText(relationship) ||
        asNormalizedText(contactType) ||
        'other';

      candidates.push({
        sourceEntryId: entry.id,
        contactName,
        contactEmail,
        relationship: relationshipValue,
        relationshipDetails: asNormalizedText(notes) || null,
        priorityOrder: safeParseNumber(priority),
      });
    }

    return candidates;
  }

  static async syncLegacyContactsFromContactsCategory(
    userId: string,
    options?: { force?: boolean },
  ): Promise<void> {
    const forceSync = options?.force ?? false;
    const now = Date.now();
    const lastSyncedAt = this.lastSyncedByUser.get(userId) ?? 0;

    if (!forceSync && now - lastSyncedAt < LEGACY_SYNC_COOLDOWN_MS) {
      return;
    }

    this.lastSyncedByUser.set(userId, now);

    const candidates =
      await this.getLegacyCandidatesFromContactsCategory(userId);
    if (candidates.length === 0) return;

    const { data: existingContacts, error: existingError } = await supabase
      .from('estate_vault_contacts')
      .select('*')
      .eq('user_id', userId)
      .eq('role', 'legacy_contact')
      .is('revoked_at', null);

    if (existingError) {
      logger.warn('Failed to load existing estate legacy contacts for sync', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(existingError),
      });
      return;
    }

    const existingByEmail = new Map<string, EstateContact>();
    for (const contact of existingContacts ?? []) {
      existingByEmail.set(String(contact.contact_email).toLowerCase(), contact);
    }

    for (const candidate of candidates) {
      const existing = existingByEmail.get(candidate.contactEmail);

      if (!existing) {
        const insertPayload: EstateContactInsert = {
          user_id: userId,
          contact_name: candidate.contactName,
          contact_email: candidate.contactEmail,
          relationship: candidate.relationship,
          relationship_details: candidate.relationshipDetails,
          priority_order: candidate.priorityOrder,
          role: 'legacy_contact',
          status: 'active',
          access_scope: {
            source: 'contacts_category',
            source_entry_id: candidate.sourceEntryId,
          },
        };

        const { error: insertError } = await supabase
          .from('estate_vault_contacts')
          .insert(insertPayload);

        if (insertError) {
          logger.warn('Failed to sync legacy contact from contacts category', {
            context: 'EstateLegacyContactService',
            error: getErrorMessage(insertError),
          });
        }
        continue;
      }

      const shouldUpdate =
        existing.contact_name !== candidate.contactName ||
        existing.relationship !== candidate.relationship ||
        existing.relationship_details !== candidate.relationshipDetails ||
        existing.priority_order !== candidate.priorityOrder;

      if (!shouldUpdate) continue;

      const { error: updateError } = await supabase
        .from('estate_vault_contacts')
        .update({
          contact_name: candidate.contactName,
          relationship: candidate.relationship,
          relationship_details: candidate.relationshipDetails,
          priority_order: candidate.priorityOrder,
        })
        .eq('id', existing.id)
        .eq('user_id', userId)
        .eq('role', 'legacy_contact');

      if (updateError) {
        logger.warn('Failed to update synced legacy contact details', {
          context: 'EstateLegacyContactService',
          error: getErrorMessage(updateError),
        });
      }
    }
  }

  /**
   * List all active legacy contacts for the given user.
   */
  static async listLegacyContacts(userId: string): Promise<LegacyContact[]> {
    await this.syncLegacyContactsFromContactsCategory(userId, { force: true });

    const { data, error } = await supabase
      .from('estate_vault_contacts')
      .select('*')
      .eq('user_id', userId)
      .eq('role', 'legacy_contact')
      .is('revoked_at', null)
      .order('priority_order', { ascending: true, nullsFirst: false });

    if (error) {
      logger.error('Failed to list legacy contacts', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(error),
      });
      throw new Error('LEGACY_CONTACT_FETCH_FAILED');
    }

    return data ?? [];
  }

  /**
   * Create a new legacy contact designation.
   */
  static async createLegacyContact(
    userId: string,
    payload: CreateLegacyContactPayload,
  ): Promise<LegacyContact> {
    const insert: EstateContactInsert = {
      user_id: userId,
      contact_name: payload.contact_name,
      contact_email: payload.contact_email,
      relationship: payload.relationship,
      relationship_details: payload.relationship_details ?? null,
      priority_order: payload.priority_order ?? null,
      role: 'legacy_contact',
      status: 'pending',
      access_scope: payload.country_code
        ? { country_code: payload.country_code }
        : null,
    };

    const { data, error } = await supabase
      .from('estate_vault_contacts')
      .insert(insert)
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to create legacy contact', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(error),
      });
      throw new Error('LEGACY_CONTACT_CREATE_FAILED');
    }

    // Trigger non-blocking workflow evaluation
    if (payload.country_code) {
      void EstateContactDocumentWorkflowService.evaluateAndPersist(
        userId,
        data.id,
        'legacy_contact',
        payload.country_code,
      ).catch((err) => {
        logger.warn('Workflow evaluation failed after legacy contact create', {
          context: 'EstateLegacyContactService',
          error: getErrorMessage(err),
        });
      });
    }

    return data;
  }

  /**
   * Update a legacy contact's details.
   */
  static async updateLegacyContact(
    userId: string,
    contactId: string,
    payload: UpdateLegacyContactPayload,
  ): Promise<LegacyContact> {
    // Extract country_code before passing to DB (it's not a DB column)
    const { country_code: countryCode, ...dbPayload } = payload;

    // If a country_code update is provided, merge it into the existing access_scope
    let updateData: typeof dbPayload & { access_scope?: unknown } = dbPayload;
    if (countryCode !== undefined) {
      const { data: existing } = await supabase
        .from('estate_vault_contacts')
        .select('access_scope')
        .eq('id', contactId)
        .eq('user_id', userId)
        .single();

      const currentScope =
        (existing?.access_scope as Record<string, unknown> | null) ?? {};
      updateData = {
        ...dbPayload,
        access_scope: { ...currentScope, country_code: countryCode },
      };
    }

    const { data, error } = await supabase
      .from('estate_vault_contacts')
      .update(updateData)
      .eq('id', contactId)
      .eq('user_id', userId)
      .eq('role', 'legacy_contact')
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to update legacy contact', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(error),
      });
      throw new Error('LEGACY_CONTACT_UPDATE_FAILED');
    }

    // Trigger non-blocking document workflow evaluation when country is known
    const resolvedCountry =
      countryCode !== undefined
        ? countryCode
        : ((data.access_scope as Record<string, unknown> | null)
            ?.country_code as string | null) ?? null;

    if (resolvedCountry) {
      void EstateContactDocumentWorkflowService.evaluateAndPersist(
        userId,
        contactId,
        'legacy_contact',
        resolvedCountry,
      ).catch((err) => {
        logger.warn('Document workflow evaluation failed after update', {
          context: 'EstateLegacyContactService',
          error: getErrorMessage(err),
        });
      });
    }

    return data;
  }

  /**
   * Revoke a legacy contact designation.
   */
  static async revokeLegacyContact(
    userId: string,
    contactId: string,
    reason: string,
  ): Promise<void> {
    const { error } = await supabase
      .from('estate_vault_contacts')
      .update({
        revoked_at: new Date().toISOString(),
        revoked_reason: reason,
        status: 'revoked',
      })
      .eq('id', contactId)
      .eq('user_id', userId)
      .eq('role', 'legacy_contact');

    if (error) {
      logger.error('Failed to revoke legacy contact', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(error),
      });
      throw new Error('LEGACY_CONTACT_REVOKE_FAILED');
    }
  }

  /**
   * List permissions configured for a specific contact.
   */
  static async listContactPermissions(
    userId: string,
    contactId: string,
  ): Promise<LegacyContactPermission[]> {
    await this.syncLegacyContactsFromContactsCategory(userId);

    const { data, error } = await supabase
      .from('estate_vault_contact_permissions')
      .select('*')
      .eq('user_id', userId)
      .eq('contact_id', contactId);

    if (error) {
      logger.error('Failed to list contact permissions', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(error),
      });
      throw new Error('LEGACY_CONTACT_PERMISSIONS_FETCH_FAILED');
    }

    return data ?? [];
  }

  /**
   * Upsert a single permission for a legacy contact.
   */
  static async upsertContactPermission(
    userId: string,
    payload: Omit<ContactPermissionInsert, 'user_id'>,
  ): Promise<LegacyContactPermission> {
    const { data, error } = await supabase
      .from('estate_vault_contact_permissions')
      .upsert({ ...payload, user_id: userId })
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to upsert contact permission', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(error),
      });
      throw new Error('LEGACY_CONTACT_PERMISSION_UPSERT_FAILED');
    }

    return data;
  }

  /**
   * Delete a specific permission entry.
   */
  static async deleteContactPermission(
    userId: string,
    permissionId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from('estate_vault_contact_permissions')
      .delete()
      .eq('id', permissionId)
      .eq('user_id', userId);

    if (error) {
      logger.error('Failed to delete contact permission', {
        context: 'EstateLegacyContactService',
        error: getErrorMessage(error),
      });
      throw new Error('LEGACY_CONTACT_PERMISSION_DELETE_FAILED');
    }
  }
}
